import parse, {
  attributesToProps,
  type DOMNode,
  domToReact,
  Element,
} from 'html-react-parser';

import { hardenIframeAttribs } from 'klue-html/iframe';
import { KLUE_CARD_NO_ZOOM_IMG } from 'lib/constants';

import { EmbedContent } from './embed-content';
import { ImageContent } from './image-content';

const options = (logEventLabel: string, cardId?: number, rank?: number) => {
  return {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element && domNode.attribs) {
        /**
         * Case 1: Transform <a> tags with "card-image-link" or "block-link" class
         *
         * Examples:
         * ```html
         * <a class="card-image-link" href="https://example.com" target="_blank">
         *   <img src="image.jpg" alt="Example" />
         * </a>
         *
         * <a class="block-link" data-attrs-type="internal" href="https://klue.d/card/123">
         *   <picture class="block-image-container">
         *     <img src="image.jpg" class="block-image" />
         *   </picture>
         * </a>
         * ```
         */
        if (
          domNode.name === 'a' &&
          (domNode.attribs.class?.includes('card-image-link') ||
            domNode.attribs.class?.includes('block-link'))
        ) {
          const { href, target } = domNode.attribs;

          // Handle both direct img children and img inside picture tags
          let childImage: Element | undefined;
          const pictureChild = domNode.children?.find(
            (node) => node instanceof Element && node.name === 'picture',
          ) as Element | undefined;

          if (pictureChild) {
            // Find img inside picture
            childImage = pictureChild.children?.find(
              (node) => node instanceof Element && node.name === 'img',
            ) as Element | undefined;
          } else {
            // Find direct img child
            childImage = domNode.children?.find(
              (node) => node instanceof Element && node.name === 'img',
            ) as Element | undefined;
          }

          const src = childImage?.attribs?.src;
          if (href && src && childImage) {
            const zoom = !childImage.attribs.class?.includes(
              KLUE_CARD_NO_ZOOM_IMG,
            );

            // Use either picture element's children or direct children depending on structure
            const contentChildren = pictureChild
              ? pictureChild.children
              : domNode.children;

            return (
              <ImageContent
                zoom={zoom}
                src={src}
                externalLink={{ href, target }}
                logEventLabel={logEventLabel}
                cardId={cardId}
                rank={rank}
              >
                <>{domToReact(contentChildren)}</>
              </ImageContent>
            );
          }
        }

        /**
         * Case 2: Transform <picture> tags with "block-image-container" class
         *
         * Examples:
         * ```html
         * <a href="https://example.com">
         *   <picture class="block-image-container">
         *     <img src="image.jpg" class="block-image" />
         *   </picture>
         * </a>
         *
         * <picture class="block-image-container">
         *   <img src="image.jpg" class="block-image" />
         * </picture>
         * ```
         */
        if (
          domNode.name === 'picture' &&
          domNode.attribs.class?.includes('block-image-container')
        ) {
          const childImage = domNode.children?.find(
            (node) => node instanceof Element && node.name === 'img',
          ) as Element | undefined;

          const src = childImage?.attribs?.src;
          const parentNode = domNode.parent;
          const hasLinkParent =
            parentNode instanceof Element && parentNode.name === 'a';

          if (src && childImage) {
            const zoom = !childImage.attribs.class?.includes(
              KLUE_CARD_NO_ZOOM_IMG,
            );

            if (hasLinkParent && parentNode instanceof Element) {
              const { href, target } = parentNode.attribs;

              return (
                <ImageContent
                  zoom={zoom}
                  src={src}
                  externalLink={{ href, target }}
                  logEventLabel={logEventLabel}
                  cardId={cardId}
                  rank={rank}
                >
                  <>{domToReact(domNode.children)}</>
                </ImageContent>
              );
            }

            return (
              <ImageContent
                {...(childImage ? attributesToProps(childImage.attribs) : {})}
                src={src}
                zoom={zoom}
                logEventLabel={logEventLabel}
                cardId={cardId}
                rank={rank}
              />
            );
          }
        }

        /**
         * Case 3: Transform standalone <img> tags
         *
         * Example:
         * ```html
         * <img src="image.jpg" alt="Example" />
         * ```
         */
        if (
          domNode.name === 'img' &&
          !domNode.attribs.class?.includes(KLUE_CARD_NO_ZOOM_IMG)
        ) {
          const src = domNode.attribs.src;
          if (src) {
            return (
              <ImageContent
                {...attributesToProps(domNode.attribs)}
                src={src}
                zoom={true}
                logEventLabel={logEventLabel}
                cardId={cardId}
                rank={rank}
              />
            );
          }
        }

        /**
         * Google Slides embeds are rendered with EmbedContent so users can
         * open the deck in a zoom modal for a larger view. Other iframes
         * fall through to the bare lazy-loaded version.
         */
        if (domNode.name === 'iframe') {
          const src = domNode.attribs.src;
          const isGoogleSlides =
            src?.includes('docs.google.com/presentation') ?? false;

          // Repeated from sanitize(): transformHTML is exported and must not
          // rely on its caller having sanitized first.
          hardenIframeAttribs(domNode.attribs);

          if (isGoogleSlides) {
            return <EmbedContent {...attributesToProps(domNode.attribs)} />;
          }

          domNode.attribs.loading = 'lazy';
          return domNode;
        }
      }
    },
  };
};

/**
 * Transforms HTML string content into React components
 *
 * @example
 * // Images
 * transformHTML('<img src="image.jpg" />', 'card-view', 123);
 *
 * // External links
 * transformHTML('<a href="https://example.com"><img src="image.jpg" /></a>', 'card-view', 123);
 *
 * // Internal links
 * transformHTML('<a class="block-link" href="/card/123"><img src="image.jpg" /></a>', 'card-view');
 */
const transformHTML = (
  html: string,
  logEventLabel: string,
  cardId?: number,
  rank?: number,
) => {
  const opt = options(logEventLabel, cardId, rank);
  return parse(html, opt);
};

export default transformHTML;
